/** French texts. Keys are typed: a missing or misspelled key is a compile error. */
export const fr = {
  app: { name: 'Prospecter' },
  nav: {
    dashboard: 'Tableau de bord',
    organizations: 'Organisations',
    contacts: 'Contacts',
    account: 'Mon compte',
    signOut: 'Se déconnecter',
    collapse: 'Réduire le menu',
    expand: 'Ouvrir le menu',
    mainNavigation: 'Navigation principale',
    userMenu: '{{name}}, menu du compte',
  },
  theme: { label: 'Thème', light: 'Clair', dark: 'Sombre', system: 'Système' },
  signIn: {
    title: 'Connexion',
    subtitle: 'Accédez à votre espace de prospection.',
    email: 'E-mail',
    password: 'Mot de passe',
    submit: 'Se connecter',
    submitting: 'Connexion…',
    wrongCredentials: 'E-mail ou mot de passe incorrect.',
    tooManyAttempts: 'Trop de tentatives échouées : réessayez dans une minute.',
    failed: 'La connexion a échoué, réessayez.',
    copyright: '© SoLean IT {{year}}',
  },
  common: {
    retry: 'Réessayer',
    save: 'Enregistrer',
    saving: 'Enregistrement…',
    cancel: 'Annuler',
    edit: 'Modifier',
    saved: 'Modification enregistrée',
    back: 'Retour',
    remove: 'Supprimer',
    uploading: 'Envoi…',
    optional: 'facultatif',
    notProvided: 'Non renseigné',
    loading: 'Chargement…',
    loadMore: 'Charger plus',
  },
  errors: {
    title: 'Une erreur est survenue',
    network: 'Impossible de joindre le serveur. Vérifiez votre connexion.',
    refresh: 'Les données n’ont pas pu être actualisées.',
    notFound: 'Page introuvable',
    notFoundDescription: 'La page demandée n’existe pas ou a été déplacée.',
    backHome: 'Retour au tableau de bord',
    imageInvalid: 'Image invalide : JPEG, PNG ou WebP de 2 Mo maximum.',
  },
  dashboard: {
    greeting: 'Bonjour {{name}}',
    subtitle: 'Vue d’ensemble de la prospection de {{organization}}.',
    stats: { activeOrganizations: 'Organisations actives', mapped: 'Organisations géolocalisées' },
    map: { title: 'Carte des organisations', empty: 'Aucune organisation géolocalisée pour l’instant.' },
    recent: { title: 'Dernières organisations', viewAll: 'Tout voir', empty: 'Aucune organisation.' },
  },
  fields: {
    jobTitle: 'Fonction',
    corporateName: 'Raison sociale',
    form: 'Forme juridique',
    type: 'Type',
    email: 'E-mail',
    phoneNumber: 'Téléphone',
    language: 'Pays de la langue',
    street: 'Rue',
    postalCode: 'Code postal',
    city: 'Ville',
    country: 'Pays',
    observations: 'Observations',
    firstName: 'Prénom',
    lastName: 'Nom',
    loginEmail: 'E-mail de connexion',
    password: 'Nouveau mot de passe',
  },
  organizations: {
    title: 'Organisations',
    subtitle: 'Les organisations que vous prospectez, les plus récentes d’abord.',
    new: 'Nouvelle organisation',
    searchPlaceholder: 'Rechercher par nom ou e-mail…',
    empty: {
      title: 'Aucune organisation',
      description: 'Commencez à prospecter une première organisation.',
    },
    noResult: { title: 'Aucun résultat', description: 'Aucune organisation ne correspond à « {{query}} ».' },
    preview: { open: 'Voir la fiche' },
    start: { title: 'Commencer à prospecter', submit: 'Commencer à prospecter' },
    sections: {
      legalIdentity: 'Identité',
      contactDetails: 'Coordonnées',
      address: 'Adresse',
      observations: 'Observations',
      holding: 'Holding',
      logo: 'Logo',
      contacts: 'Contacts',
    },
    contacts: {
      none: 'Aucun contact rattaché.',
      unnamed: 'Contact sans nom',
      masked: 'Masqué',
      remove: 'Retirer {{name}} des contacts',
      search: 'Rechercher un de mes contacts…',
      add: 'Ajouter un contact',
      addNew: 'Nouveau contact',
      addExisting: 'Contact existant',
      identity: 'Identité',
      collectSubmit: 'Enregistrer le contact',
      removeTitle: 'Retirer {{name}} de cette organisation ?',
      removeDescription:
        'Si ce contact n’est rattaché à aucune autre organisation, sa fiche et ses données personnelles seront effacées.',
      removeConfirm: 'Retirer',
    },
    stop: {
      action: 'Arrêter de prospecter',
      title: 'Arrêter de prospecter {{name}} ?',
      description:
        'L’organisation sort de votre base de prospection, ses filiales n’ont plus de holding, et ses contacts rattachés à aucune autre organisation sont effacés.',
      confirm: 'Arrêter définitivement',
    },
    address: { noLocation: 'Adresse non géolocalisée.', directions: 'Itinéraire' },
    holding: {
      none: 'Rattachée à aucune holding.',
      detach: 'Détacher',
      search: 'Rechercher une de mes organisations…',
    },
    logo: { change: 'Changer le logo', add: 'Ajouter un logo' },
    validation: { nameOrEmail: 'Renseignez au moins une raison sociale ou un e-mail.' },
  },
  contacts: {
    title: 'Contacts',
    subtitle: 'Les personnes que vous prospectez, les plus récentes d’abord.',
    searchPlaceholder: 'Rechercher par nom ou e-mail…',
    empty: {
      title: 'Aucun contact',
      description: 'Les contacts s’enregistrent depuis la fiche de l’organisation où ils travaillent.',
    },
    noResult: { title: 'Aucun résultat', description: 'Aucun contact ne correspond à « {{query}} ».' },
    unnamed: 'Contact sans nom',
    masked: 'Données masquées',
    organizationsOf: 'Organisations de {{name}}',
    sections: {
      name: 'Nom',
      jobTitle: 'Fonction',
      contactDetails: 'Coordonnées',
      organizations: 'Organisations',
    },
    forget: {
      action: 'Effacer la fiche',
      title: 'Effacer la fiche de {{name}} ?',
      description:
        'Ses données personnelles seront supprimées définitivement, et elle sera retirée de toutes ses organisations.',
      confirm: 'Effacer définitivement',
    },
  },
  account: {
    title: 'Mon compte',
    subtitle: 'Vos informations personnelles et celles de votre organisation.',
    sections: {
      picture: 'Photo de profil',
      name: 'Nom',
      loginEmail: 'E-mail de connexion',
      contactDetails: 'Coordonnées',
      password: 'Mot de passe',
      organizationIdentity: 'Mon organisation',
      organizationLogo: 'Logo de mon organisation',
    },
    changePicture: 'Changer la photo',
    changeLogo: 'Changer le logo',
  },
  validation: {
    required: 'Ce champ est obligatoire.',
    email: 'Adresse e-mail invalide.',
    max: '{{max}} caractères maximum.',
    min: '{{min}} caractères minimum.',
  },
} as const

export type Messages = typeof fr
